import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Car } from 'lucide-react';
import { TabContent } from './TabContent';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import type { DisplayJob } from '../../../../../types';

export interface JobDetailsSectionProps {
  displayJob: DisplayJob;
  specialisations?: string[];
  optionalLanguages?: string[];
  perks?: string[];
  originalJobText?: string | null;
  activeTab: 'Benefits' | 'Recruitment' | 'Skills';
  onTabChange: (tab: 'Benefits' | 'Recruitment' | 'Skills') => void;
  activeTabStyle?: React.CSSProperties;
  jobDetailsRef: React.RefObject<HTMLDivElement>;
}

export const JobDetailsSection: React.FC<JobDetailsSectionProps> = ({
  displayJob,
  specialisations,
  optionalLanguages,
  perks,
  originalJobText,
  activeTab,
  onTabChange,
  activeTabStyle,
  jobDetailsRef,
}) => {
  const intl = useIntl();
  const [showOriginalJobText, setShowOriginalJobText] = useState(false);

  // Calculate last seen freshness
  const lastSeenFreshness = displayJob?.lastSeen ? (() => {
    const now = new Date();
    const lastSeenDate = new Date(displayJob.lastSeen);
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    let freshnessId = 'hit.lastSeen.daysAgo';
    let freshnessValue: number = diffDays;
    if (diffMins < 120) {
      freshnessId = 'hit.lastSeen.minutesAgo';
      freshnessValue = diffMins;
    } else if (diffHours < 24) {
      freshnessId = 'hit.lastSeen.hoursAgo';
      freshnessValue = diffHours;
    }
    return { freshnessId, freshnessValue };
  })() : null;

  // Check if content is complete for schema
  const isContentComplete = !!(
    displayJob?.title &&
    displayJob?.description &&
    displayJob?.companyId &&
    displayJob?.location &&
    (displayJob?.salaryRangeMin && displayJob?.salaryRangeMax) &&
    displayJob?.jobType &&
    displayJob?.normalisedRoleLevel
  );

  const missingFields = [];
  if (!displayJob?.title) missingFields.push('Title');
  if (!displayJob?.description) missingFields.push('Description');
  if (!displayJob?.companyId) missingFields.push('Company');
  if (!displayJob?.location) missingFields.push('Location');
  if (!displayJob?.salaryRangeMin || !displayJob?.salaryRangeMax) missingFields.push('Salary');
  if (!displayJob?.jobType) missingFields.push('Job Type');
  if (!displayJob?.normalisedRoleLevel) missingFields.push('Role Level');
  const missingTooltip = missingFields.length > 0 
    ? `Missing: ${missingFields.join(', ')}` 
    : 'Content Complete';

  return (
    <div ref={jobDetailsRef} data-section="jobDetails" className="scroll-mt-20 mt-10 pt-10 border-t border-gray-200/60 dark:border-gray-700/60" id="jobDetails">
      <h2 id="jobDetails-header" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
        <FormattedMessage 
          id="jobDetailPage.section.jobDetails" 
          defaultMessage="Job Details" 
          description="Tab label for detailed job requirements section" 
        />
      </h2>

      {/* Job Details - 2 Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Requirements */}
          {displayJob.requirements && displayJob.requirements.length > 0 && (
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                <FormattedMessage 
                  id="jobDetailPage.requirements" 
                  defaultMessage="Requirements" 
                  description="Section heading for job requirements list" 
                />
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {displayJob.requirements.map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Responsibilities */}
          {displayJob.responsibilities && displayJob.responsibilities.length > 0 && (
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                <FormattedMessage 
                  id="jobDetailPage.responsibilities" 
                  defaultMessage="Responsibilities" 
                  description="Section heading for job responsibilities list" 
                />
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {displayJob.responsibilities.map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Field */}
          {displayJob.field && (
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                <FormattedMessage 
                  id="jobDetailPage.field" 
                  defaultMessage="Field" 
                  description="Section label for job field/domain (e.g., Software Development, Marketing)" 
                />
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">{displayJob.field}</p>
            </div>
          )}
          
          {/* Education Level */}
          {displayJob.educationLevel && (
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                <FormattedMessage 
                  id="search.refinements.educationLevel.detail" 
                  defaultMessage="Education Level" 
                  description="Education level section heading on job detail page" 
                />
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300">{displayJob.educationLevel}</p>
            </div>
          )}
        </div>
        
        {/* Right Column */}
        <div className="space-y-6">
          {/* Specialisations */}
          {specialisations && specialisations.length > 0 && (
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                <FormattedMessage 
                  id="jobDetailPage.specialisations" 
                  defaultMessage="Specialisations" 
                  description="Section heading for job specializations or focus areas" 
                />
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {specialisations.map((spec: string, index: number) => (
                  <li key={index}>{fixMojibake(spec)}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Optional Languages */}
          {optionalLanguages && optionalLanguages.length > 0 && (
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                <FormattedMessage 
                  id="jobDetailPage.optionalLanguages" 
                  defaultMessage="Optional Languages" 
                  description="Section heading for list of optional/preferred languages for the job" 
                />
              </h3>
              <div className="flex flex-wrap gap-2">
                {optionalLanguages.map((lang: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-sm font-medium border border-blue-200 dark:border-blue-700"
                  >
                    {fixMojibake(lang)}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Perks */}
          {perks && perks.length > 0 && (
            <div>
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                <FormattedMessage 
                  id="jobDetailPage.perks" 
                  defaultMessage="Perks" 
                  description="Section heading for job perks and benefits list" 
                />
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {perks.map((perk: string, index: number) => (
                  <li key={index}>{fixMojibake(perk)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Tabbed Section for Benefits, Recruitment, Skills */}
      <div id="jobDetails-benefits" className="pt-4 scroll-mt-20">
        <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
          {['Benefits', 'Recruitment', 'Skills'].map((tab) => (
            <button
              key={tab}
              onClick={() => onTabChange(tab as 'Benefits' | 'Recruitment' | 'Skills')}
              className={`pb-2 cursor-pointer ${
                activeTab === tab ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'
              }`}
              style={activeTab === tab ? activeTabStyle : {}}
            >
              {tab}
            </button>
          ))}
        </div>
        <TabContent
          activeTab={activeTab}
          benefits={displayJob?.benefits}
          recruitmentProcess={displayJob?.recruitmentProcess}
          skillsAndTechnologies={displayJob?.skillsAndTechnologies}
        />
      </div>

      {/* Original Job Text Section - COMMENTED OUT FOR PERFORMANCE TESTING */}
      {/* {originalJobText && (
        <div className="mt-6 pt-6 border-t border-gray-200/60 dark:border-gray-700/60">
          {!showOriginalJobText ? (
            <button
              type="button"
              onClick={() => setShowOriginalJobText(true)}
              className="text-sm text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 px-2 py-1 border border-blue-100 dark:border-blue-700 rounded transition-colors cursor-pointer"
            >
              <FormattedMessage 
                id="jobDetailPage.showOriginalJobText" 
                defaultMessage="Show Original Job Text" 
                description="Button label to reveal the original job posting text before translation" 
              />
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  <FormattedMessage 
                    id="jobDetailPage.originalJobText" 
                    defaultMessage="Original Job Text" 
                    description="Section heading for the original untranslated job posting text" 
                  />
                </h3>
                <button
                  type="button"
                  onClick={() => setShowOriginalJobText(false)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 underline cursor-pointer"
                >
                  <FormattedMessage 
                    id="jobDetailPage.hideOriginalJobText" 
                    defaultMessage="Hide" 
                    description="Button label to hide the original job text section" 
                  />
                </button>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200/60 dark:border-gray-700/60">
                  {fixMojibake(originalJobText)}
                </div>
              </div>
            </div>
          )}
        </div>
      )} */}

      {/* Bottom row: Last Seen (left), Schema badge (right) */}
      <div className="flex flex-row justify-between items-end mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        {/* Last Seen freshness indicator */}
        {lastSeenFreshness ? (
          <span className="text-green-600 dark:text-green-400 text-xs font-semibold" title={displayJob.lastSeen}>
            <FormattedMessage
              id={lastSeenFreshness.freshnessId}
              defaultMessage={
                lastSeenFreshness.freshnessId === 'hit.lastSeen.minutesAgo'
                  ? '{value, plural, one {Seen {value} minute ago} other {Seen {value} minutes ago}}'
                  : lastSeenFreshness.freshnessId === 'hit.lastSeen.hoursAgo'
                  ? '{value, plural, one {Seen {value} hour ago} other {Seen {value} hours ago}}'
                  : '{value, plural, one {Seen {value} day ago} other {Seen {value} days ago}}'
              }
              values={{ value: lastSeenFreshness.freshnessValue }}
              description="Indicates how recently the job was last seen/crawled."
            />
          </span>
        ) : (
          <span />
        )}
        {/* Schema badge and job flags */}
        <span className="flex flex-row gap-2 items-center">
          {/* Job flag badges */}
          {displayJob?.isSummerJob && (
            <span
              className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-semibold border border-yellow-200"
              title="Summer Job"
            >
              <FormattedMessage 
                id="hit.summerJob" 
                defaultMessage="Summer Job" 
                description="Badge label indicating this is a summer/seasonal job position" 
              />
            </span>
          )}
          {displayJob?.isDriversLicenseRequired && (
            <div className="relative group">
              <Car className="h-4 w-4 text-blue-600 dark:text-blue-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                <FormattedMessage 
                  id="hit.driversLicenseRequired" 
                  defaultMessage="Driver's Licence Required" 
                  description="Badge label indicating this job requires a valid driver's license" 
                />
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
              </div>
            </div>
          )}
          {displayJob?.isOpenApplication && (
            <span
              className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-semibold border border-green-200"
              title="Open Application"
            >
              <FormattedMessage 
                id="hit.openApplication" 
                defaultMessage="Open Application" 
                description="Badge label indicating this is an open application position (accepting general applications, not for a specific role)" 
              />
            </span>
          )}
          {/* Schema badge */}
          {isContentComplete ? (
            <img
              src="/data-analysis.png"
              alt="Content Complete"
              className="h-5 w-5 rounded-full shadow border border-gray-200 dark:border-gray-700"
              title="This job is content complete for Google and schema.org. Eligible for full rich results."
              aria-label="Content Complete"
              loading="lazy"
            />
          ) : (
            <img
              src="/data-collection.png"
              alt="Partial Schema"
              className="h-5 w-5 rounded-full shadow border border-gray-200 dark:border-gray-700"
              title={missingTooltip}
              aria-label="Partial Schema"
              loading="lazy"
            />
          )}
        </span>
      </div>
    </div>
  );
};
